import {Route, Routes} from '@angular/router';
import {inject} from '@angular/core';

import {AuthGuard} from '@auth/state/auth-guard';
import {PublicLayoutComponent} from './public/layout.component';
import {DefaultLayoutComponent} from './default/layout.component';
import {SecurityCondition} from '@auth/state/security-condition';
import {
  CLIENT_ONLY,
  CUSTOMER,
  CUSTOMER_ADMIN,
  CUSTOMER_RELATED,
  CUSTOMER_RELATED_NO_FIRM,
  EMPLOYEE_CLIENT_ASSIGN,
  OTHER_THAN_CLIENT,
  TRANSACTION_RULE_BULK_UPDATE
} from '@auth/constants';
import {clientSelectedGuard, customerHasConnection} from '@auth/state/client-selected.guard';
import {TAccountingPlatform} from '@shared/interface';
import {LocalStorageService} from '@services/local-storage.service';

export const routes: Routes = [
  {
    // AI connector sign-in page (card #1310). Top level on purpose: inside DefaultLayoutComponent it would get
    // the header, sidebar, user guide tours and the bank MFA pop-up. It shows role refusals itself, so no role.
    path: 'connect-ai',
    loadComponent: () => import('../pages/connect-ai/connect-ai.component').then(x => x.ConnectAiComponent),
    canActivate: [AuthGuard],
    data: {
      security: SecurityCondition.authenticated(),
    },
  },
  {
    // Zoho Desk help center sign-in (card #1412). The path is registered in Zoho, do not change it. Top level like
    // connect-ai: the page only redirects, so no header, sidebar, tours or pop-ups.
    path: 'support/help-center',
    loadComponent: () => import('../pages/support-help-center/support-help-center.component')
    .then(x => x.SupportHelpCenterComponent),
    canActivate: [AuthGuard],
    data: {
      security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
    },
  },
  {
    path: '',
    component: DefaultLayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('../pages/home/home.component').then(x => x.HomeComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'client-profile',
        pathMatch: 'full',
        loadComponent: () => import('../client-profile/client-profile.component')
        .then(x => x.ClientProfileComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(CLIENT_ONLY)
        }
      },
      {
        path: 'crm',
        loadComponent: () => import('../pages/crm/crm.component')
        .then(x => x.CrmComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'communication-center',
          },
          {
            path: 'client-list',
            loadComponent: () => import('../pages/crm/client-list/client-list.component')
            .then(x => x.ClientListComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: `client-list/:integration/auth/complete`,
            loadComponent: () => import('../pages/crm/client-list/client-list.component')
            .then(x => x.ClientListComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'communication-center',
            loadComponent: () => import('../pages/crm/communication-center/communication-center.component')
            .then(x => x.CommunicationCenterComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'client-groups',
            loadComponent: () => import('../pages/crm/groups/groups.component')
            .then(x => x.GroupsComponent),
            canActivate: [AuthGuard],
            data: {
              security: SecurityCondition.hasRole(CUSTOMER_RELATED),
            },
          },
        ]
      },
      {
        path: 'task-center',
        loadComponent: () => import('../pages/task-center/task-center.component')
        .then(x => x.TaskCenterComponent),
        canActivateChild: [AuthGuard],
        data: {
          security: SecurityCondition.authenticated()
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: () => {
              const role = inject(LocalStorageService).getCurrentUserDetails()?.role;
              return role === CUSTOMER || role === CUSTOMER_ADMIN ? 'all' : 'assigned-to-me';
            },
          },
          {
            path: 'all',
            loadComponent: () => import('../pages/task-center/task-center-view/task-center-view.component')
            .then(x => x.TaskCenterViewComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'assigned-to-me',
            loadComponent: () => import('../pages/task-center/task-center-view/task-center-view.component')
            .then(x => x.TaskCenterViewComponent),
            data: {
              security: SecurityCondition.authenticated(),
            },
          },
          {
            path: 'created-by-me',
            loadComponent: () => import('../pages/task-center/task-center-view/task-center-view.component')
            .then(x => x.TaskCenterViewComponent),
            data: {
              security: SecurityCondition.authenticated(),
            },
          },
          {
            path: 'messages',
            loadComponent: () => import('../pages/task-center/task-center-messages-view/task-center-messages-view.component')
            .then(x => x.TaskCenterMessagesViewComponent),
            data: {
              security: SecurityCondition.authenticated(),
            },
          },
          {
            path: 'recurrent-tasks',
            loadComponent: () => import('../pages/task-center/recurrent-tasks-view/recurrent-tasks-view.component')
            .then(x => x.RecurrentTasksViewComponent),
            data: {
              security: SecurityCondition.hasRole(CUSTOMER_RELATED_NO_FIRM),
            },
          },
        ],
      },
      {
        path: 'employees',
        loadComponent: () => import('../pages/employees/employees.component')
        .then(x => x.EmployeesComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRoleOrZone(CUSTOMER_RELATED, [EMPLOYEE_CLIENT_ASSIGN])
        },
      },
      {
        path: 'firms',
        loadComponent: () => import('../pages/firms/firms.component')
        .then(x => x.FirmsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole([CUSTOMER]),
        },
      },
      {
        path: 'notes',
        loadComponent: () => import('../pages/notes/notes.component')
        .then(x => x.NotesComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
      },
      {
        path: 'pdf-converter',
        loadComponent: () => import('../pages/pdf-converter/pdf-converter.component')
        .then(x => x.PdfConverterComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
      },
      {
        path: 'supported-institutions',
        loadComponent: () => import('../pages/supported-institutions/supported-institutions.component')
        .then(x => x.SupportedInstitutionsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
      },
      {
        path: 'quickbooks-transactions',
        loadComponent: () => import('../pages/quickbooks-transactions/quickbooks-transactions.component')
        .then(x => x.QuickbooksTransactionsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
      },
      // old routing, in case someone has saved the url
      {
        path: 'summary',
        redirectTo: '/banks/connections',
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'transactions',
        redirectTo: '/banks/transactions',
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'running-balance',
        redirectTo: '/banks/running-balance',
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'ls-created-statements',
        loadComponent: () => import('../pages/ledgersync-created-statements/ledgersync-created-statements.component')
        .then(x => x.LedgersyncCreatedStatementsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.authenticated()
        },
      },
      {
        path: 'statements',
        redirectTo: '/banks/statements',
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'checks',
        redirectTo: '/banks/checks',
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      // --- old routing, in case someone has saved the url
      {
        path: 'banks',
        loadComponent: () => import('../pages/banks/banks.component')
        .then(x => x.BanksComponent),
        canActivateChild: [AuthGuard],
        data: {
          security: SecurityCondition.authenticated()
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'connections'
          },
          {
            path: 'connections',
            loadComponent: () => import('../pages/banks/connections/connections.component')
            .then(x => x.ConnectionsComponent),
            data: {
              security: SecurityCondition.authenticated()
            },
          },
          {
            path: 'transactions',
            loadComponent: () => import('../pages/transactions/transactions.component')
            .then(x => x.TransactionsComponent),
            data: {
              security: SecurityCondition.authenticated()
            },
          },
          {
            path: 'transactions-verify',
            loadComponent: () => import('../pages/transactions-reconciliation/transactions-reconciliation.component')
            .then(x => x.TransactionsReconciliationComponent),
            data: {
              security: SecurityCondition.authenticated()
            },
          },
          {
            path: 'running-balance',
            loadComponent: () => import('../pages/running-balance/running-balance.component')
            .then(x => x.RunningBalanceComponent),
            data: {
              security: SecurityCondition.authenticated()
            },
          },
          {
            path: 'statements',
            loadComponent: () => import('../pages/statements/statements.component')
            .then(x => x.StatementsComponent),
            data: {
              security: SecurityCondition.authenticated()
            },
          },
          {
            path: 'checks',
            loadComponent: () => import('../pages/checks-images/checks-images.component')
            .then(x => x.ChecksImagesComponent),
            data: {
              security: SecurityCondition.authenticated()
            },
          },
        ]
      },
      {
        path: 'statement-reconcile/:statementId',
        loadComponent: () => import('../statement-reconcile/statement-reconcile.component')
        .then(x => x.StatementReconcileComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'transactions-reconcile',
        loadComponent: () => import('../pages/transactions-reconciliation/transactions-reconciliation.component')
        .then(x => x.TransactionsReconciliationComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'transactions-reconcile/:statementId',
        loadComponent: () => import('../pages/transactions-reconciliation/transactions-reconciliation.component')
        .then(x => x.TransactionsReconciliationComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'change-password',
        loadComponent: () => import('../change-password/change-password.component')
        .then(x => x.ChangePasswordComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.authenticated(),
        }
      },
      {
        path: 'billing',
        loadComponent: () => import('../pages/billing/billing.component').then(x => x.BillingComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole([CUSTOMER])
        },
      },
      {
        path: 'billing-tiered',
        loadComponent: () => import('../pages/billing/billing-tiered.component').then(x => x.BillingTieredComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole([CUSTOMER])
        },
      },
      {
        path: 'sq-management',
        loadComponent: () => import('../security-questions/security-questions.component')
        .then(x => x.SecurityQuestionsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
        children: [
          {
            path: 'edit',
            loadComponent: () => import('../security-questions/edit/edit.component')
            .then(x => x.EditComponent),
            data: {
              security: SecurityCondition.authenticated(),
            },
          },
        ],
      },
      {
        path: 'cloud-storage',
        loadComponent: () => import('../pages/cloud-storage/cloud-storage.component')
        .then(x => x.CloudStorageComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'smart-vault',
          },
          {
            path: 'sharepoint',
            loadComponent: () => import('../pages/cloud-storage/storages/sharepoint/sharepoint.component')
            .then(x => x.SharepointComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'dropbox',
            loadComponent: () => import('../pages/cloud-storage/storages/dropbox/dropbox.component')
            .then(x => x.DropboxComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'google-drive',
            loadComponent: () => import('../pages/cloud-storage/storages/google-drive/google-drive.component')
            .then(x => x.GoogleDriveComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'box-com',
            loadComponent: () => import('../pages/cloud-storage/storages/box-com/box-com.component')
            .then(x => x.BoxComComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'onedrive',
            loadComponent: () => import('../pages/cloud-storage/storages/onedrive/onedrive.component')
            .then(x => x.OnedriveComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'xero',
            loadComponent: () => import('../pages/cloud-storage/storages/xero/xero.component')
            .then(x => x.XeroComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
          {
            path: 'smart-vault',
            loadComponent: () => import('../pages/cloud-storage/storages/smart-vault/smart-vault.component')
            .then(x => x.SmartVaultComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
          },
        ]
      },
      {
        path: 'cloud-storage/:storageId/auth/complete',
        loadComponent: () => import('../pages/cloud-storage/connect/connect.component')
        .then(x => x.ConnectComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
        },
      },
      {
        path: 'document-storage',
        loadComponent: () => import('../document-storage/document-storage.component')
        .then(x => x.DocumentStorageComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'document-storage/folder/:folderId',
        loadComponent: () => import('../document-storage/document-storage.component')
        .then(x => x.DocumentStorageComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
      {
        path: 'settings',
        loadComponent: () => import('../pages/settings/settings.component')
        .then(x => x.SettingsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.authenticated(),
        },
      },

      {
        path: 'settings/customization',
        loadComponent: () => import('../user-customization/user-customization')
        .then(x => x.UserCustomizationComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole([CUSTOMER]),
        },
      },
      // {
      //     path: 'quickbooks',
      //     loadChildren: () => import('../qbo/page/page.module')
      //         .then(x => x.PageModule),
      //     canActivateChild: [AuthGuard]
      // },

      {...getAccountingByPlatform('quickbooks')},
      {...getAccountingByPlatform('zohobooks')},
      {...getAccountingByPlatform('manual')},
      {
        path: 'customer-admin',
        loadComponent: () => import('../pages/customer-admin/customer-admin.component')
        .then(x => x.CustomerAdminComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(CUSTOMER_RELATED_NO_FIRM)
        }
      },
      {
        path: 'permissions',
        loadComponent: () => import('../pages/permissions/permissions.component')
        .then(x => x.PermissionsComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(CUSTOMER_RELATED_NO_FIRM)
        }
      },
      {
        path: 'quickbooks-close',
        loadComponent: () => import('../pages/quickbooks-reconciliation/quickbooks-reconciliation.component')
        .then(x => x.QuickbooksReconciliationComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(CUSTOMER_RELATED_NO_FIRM)
        }
      },
      {
        path: 'mini-app-connect',
        loadComponent: () => import('../pages/mini-app-connect/mini-app-connect.component')
        .then(x => x.MiniAppConnectComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT)
        }
      },
    ]
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'login',
        loadComponent: () => import('../sign-in/sign-in.component').then(x => x.SignInComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'login-admin',
        loadComponent: () => import('../sign-in-admin/sign-in-admin.component').then(x => x.SignInAdminComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up',
        loadComponent: () => import('../sign-up/register-user/register-user.component')
        .then(x => x.RegisterUserComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'lender-sign-up',
        loadComponent: () => import('../sign-up/register-user/register-user.component')
        .then(x => x.RegisterUserComponent),
        data: {isLender: true},
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up-by-phone',
        loadComponent: () => import('../sign-up/register-user-with-phone/register-user-with-phone.component')
        .then(x => x.RegisterUserWithPhoneComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up-admin',
        loadComponent: () => import('../sign-up/register-admin/register-admin.component')
        .then(x => x.RegisterAdminComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/plans/:username',
        loadComponent: () => import('../sign-up/plan-list/plan-list.component').then(x => x.PlanListComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/order',
        loadComponent: () => import('../sign-up/order-confirm/order-confirm')
        .then(x => x.OrderConfirmComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/order/:username',
        loadComponent: () => import('../sign-up/order-confirm/order-confirm')
        .then(x => x.OrderConfirmComponent),
        canActivate: [AuthGuard]
      },
      {
        // AI connector sign-up (card #1310): the normal registration form with the email typed on the connect
        // sign-in filled in. Signed-out visitors only, like the other sign-up pages.
        path: 'sign-up/connect-ai',
        loadComponent: () => import('../pages/connect-ai/connect-ai-sign-up.component')
        .then(x => x.ConnectAiSignUpComponent),
        canActivate: [AuthGuard],
        data: {
          security: {
            publicPage: true,
          },
        },
      },
      {
        path: 'registration/complete',
        loadComponent: () => import('../sign-up/finicity-registration/finicity-registration.component')
        .then(x => x.FinicityRegistrationComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'recovery-password',
        loadComponent: () => import('../reset-password/confirm-reset-password/confirm-reset-password.component')
        .then(x => x.ConfirmResetPassword),
        canActivate: [AuthGuard],
        data: {
          security: {
            publicPage: true,
          },
        },
      },
      {
        path: 'password/setup',
        loadComponent: () => import('../reset-password/confirm-reset-password/confirm-reset-password.component')
        .then(x => x.ConfirmResetPassword),
        canActivate: [AuthGuard],
        data: {
          security: {
            publicPage: true,
          },
        },
      },
      {
        path: 'reset-password',
        loadComponent: () => import('../reset-password/reset-password.component')
        .then(x => x.ResetPasswordComponent),
        canActivate: [AuthGuard],
      },
      {
        path: 'forgot-username',
        loadComponent: () => import('../forgot-username/forgot-username.component')
        .then(x => x.ForgotUsernameComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'xero/sign-up',
        loadComponent: () => import('../sign-up/xero-sign-up/xero-sign-up.component')
        .then(x => x.XeroSignUpComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'xero/auth/complete',
        loadComponent: () => import('../sign-up/xero-sign-up/xero-sign-up.component')
        .then(x => x.XeroSignUpComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/xero',
        loadComponent: () => import('../sign-up/register-user/register-user.component')
        .then(x => x.RegisterUserComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/phone-verification',
        loadComponent: () => import('../sign-up/phone-verification/phone-verification.component')
        .then(x => x.PhoneVerificationComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/phone-verification/:username',
        loadComponent: () => import('../sign-up/phone-verification/phone-verification.component')
        .then(x => x.PhoneVerificationComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'sign-up/email-verification/:username',
        loadComponent: () => import('../sign-up/email-verification/email-verification.component')
        .then(x => x.EmailVerificationComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'page/:code',
        loadComponent: () => import('../shortened-url/shortened-url.component')
        .then(x => x.ShortenedUrlComponent),
      },
      {
        path: 'redirect-page',
        loadComponent: () => import('../redirect-page/redirect-page.component')
        .then(x => x.RedirectPageComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'thank-you',
        loadComponent: () => import('../pages/public/thank-you/thank-you.component')
        .then(x => x.ThankYouComponent),
        canActivate: [AuthGuard],
        data: {
          security: SecurityCondition.anonymous(),
        },
      },
      {
        path: 'what-is-it',
        loadComponent: () => import('../pages/what-is-it/what-is-it.component')
        .then(x => x.WhatIsItComponent),
      },
      {
        path: 'supported-banks',
        loadComponent: () => import('../pages/public/supported-banks/supported-banks.component')
        .then(x => x.SupportedBanksComponent),
      },
      {
        path: 'fix-connection/:token',
        loadComponent: () => import('../pages/fix-connection-public/fix-connection-public.component')
        .then(x => x.FixConnectionPublicComponent),
      },
      {
        path: 'reactivate',
        loadComponent: () => import('../pages/reactivation-public/reactivation-public.component')
        .then(x => x.ReactivationPublicComponent),
      }
    ]
  },
  {
    path: '**',
    redirectTo: ''
  }
];

function getAccountingByPlatform(accountingPlatform: TAccountingPlatform): Route {
  return {
    path: accountingPlatform,
    loadComponent: () => import('../pages/accounting/accounting.component')
    .then(x => x.AccountingComponent),
    canActivateChild: [AuthGuard],
    data: {
      security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
      accountingPlatform,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'transactions',
      },
      {
        path: 'auth/complete',
        loadComponent: () => import('../pages/accounting/auth-complete.component')
        .then(x => x.AuthCompleteComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
      },
      ...(
        accountingPlatform !== 'manual' ?
          [{
            path: 'transactions',
            loadComponent: () => import('../pages/accounting/transactions/transactions-nav.component')
            .then(x => x.TransactionsNavComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
            },
            canActivateChild: [customerHasConnection],
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'list',
              },
              {
                path: 'list',
                loadComponent: () => import('../pages/accounting/transactions/transactions.component')
                .then(x => x.TransactionsComponent),
                data: {
                  security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
                  accountingPlatform,
                },
              },
              {
                path: 'sync-status',
                loadComponent: () => import('../pages/accounting/transactions/sync-status/sync-status.component')
                .then(x => x.SyncStatusComponent),
                data: {
                  security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
                  accountingPlatform,
                },
              },
              ...(accountingPlatform === 'quickbooks' ? [{
                path: 'intercompany-transfers',
                loadComponent: () => import('../pages/accounting/transactions/intercompany-transfers/intercompany-transfers.component')
                .then(x => x.IntercompanyTransfersComponent),
                data: {
                  security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
                  accountingPlatform,
                },
              }] : []),
            ],
          }] :
          [{
            path: 'transactions',
            loadComponent: () => import('../pages/accounting/transactions/manual-transactions-nav.component')
            .then(x => x.ManualTransactionsNavComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
              accountingPlatform,
            },
            canActivate: [clientSelectedGuard],
          }]
      ) as Route[],
      {
        path: 'rules',
        loadComponent: () => import('../pages/accounting/initial-rules.component')
        .then(x => x.InitialRulesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
        },
      },
      {
        path: 'global-rules',
        loadComponent: () => import('../pages/accounting/global-rules-nav.component')
        .then(x => x.GlobalRulesNavComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
        },
        canActivateChild: [customerHasConnection],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'list',
          },
          {
            path: 'list',
            loadComponent: () => import('../pages/accounting/rules/rules.component')
            .then(x => x.RulesComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
              accountingPlatform,
              globalRules: true,
            },
          },
          {
            path: 'apply-status',
            loadComponent: () => import('../pages/accounting/apply-status/apply-status.component')
            .then(x => x.ApplyStatusComponent),
            data: {
              security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
              accountingPlatform,
            },
          },
        ],
      },
      {
        path: 'global-rules-ai',
        loadComponent: () => import('../pages/accounting/global-rules-ai/global-rules-ai.component')
        .then(x => x.GlobalRulesAiComponent),
        data: {
          security: SecurityCondition.hasZone([TRANSACTION_RULE_BULK_UPDATE]),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'client-rules',
        loadComponent: () => import('../pages/accounting/rules/rules.component')
        .then(x => x.RulesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [clientSelectedGuard],
      },
      {
        path: 'all-rules',
        loadComponent: () => import('../pages/accounting/rules/rules.component')
        .then(x => x.RulesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
          allRules: true,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'settings',
        loadComponent: () => import('../pages/accounting/initial-settings.component')
        .then(x => x.InitialSettingsComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'audit-logs',
        loadComponent: () => import('../pages/accounting/audit-logs/audit-logs.component')
        .then(x => x.AuditLogsComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'rules-engine-audit',
        loadComponent: () => import('../pages/accounting/rules-engine-audit/rules-engine-audit.component')
        .then(x => x.RulesEngineAuditComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'global-settings',
        loadComponent: () => import('../pages/accounting/initial-global-settings.component')
        .then(x => x.InitialGlobalSettingsComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'global-categories-settings',
        loadComponent: () => import('../pages/accounting/global-categories/global-categories.component')
        .then(x => x.GlobalCategoriesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'category-aliases-settings',
        loadComponent: () => import('../pages/accounting/category-aliases/category-aliases.component')
        .then(x => x.CategoryAliasesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'global-vendors-settings',
        loadComponent: () => import('../pages/accounting/vendors/vendors.component')
        .then(x => x.VendorsComponent),
        data: {
          global: true,
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },

      {
        path: 'client-settings',
        loadComponent: () => import('../pages/accounting/initial-client-settings.component')
        .then(x => x.InitialClientSettingsComponent),
        data: {
          global: true,
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: 'client-categories-settings',
        loadComponent: () => import('../pages/accounting/client-categories/client-categories.component')
        .then(x => x.ClientCategoriesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [clientSelectedGuard],
      },
      {
        path: 'client-vendors-settings',
        loadComponent: () => import('../pages/accounting/vendors/vendors.component')
        .then(x => x.VendorsComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [clientSelectedGuard],
      },
      {
        path: 'client-classes-settings',
        loadComponent: () => import('../pages/quickbooks/qbo-classes/qbo-classes.component')
        .then(x => x.QboClassesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [clientSelectedGuard],
      },
      ...(
        accountingPlatform !== 'manual' ?
          [
            {
              path: 'manage-authorizations',
              loadComponent: () => import('../pages/accounting/authorizations/authorizations.component')
              .then(x => x.AuthorizationsComponent),
              data: {
                security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
                accountingPlatform,
              },
              canActivate: [customerHasConnection],
            } as Route
          ] :
          []
      ),
      {
        path: 'initial',
        loadComponent: () => import('../pages/accounting/initial/initial.component')
        .then(x => x.InitialComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
      },

      {
        path: 'initial/:step',
        loadComponent: () => import('../pages/accounting/initial/initial.component')
        .then(x => x.InitialComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
      },
      ...(
        accountingPlatform === 'manual' ?
          [
            {
              path: 'manual-setup',
              loadComponent: () => import('../pages/accounting/manual-setup/manual-setup.component')
              .then(x => x.ManualSetupComponent),
              data: {
                security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
                accountingPlatform,
              },
            } as Route
          ] :
          []
      ),
      ...(
        accountingPlatform !== 'manual' ?
          [
            {
              path: 'accounts-mapping',
              loadComponent: () => import('../pages/accounting/accounts-mapping/accounts-mapping.component')
              .then(x => x.AccountsMappingComponent),
              data: {
                security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
                accountingPlatform,
              },
              canActivate: [clientSelectedGuard],
            } as Route
          ] :
          []
      ),
      {
        path: 'categories-mapping',
        pathMatch: 'full',
        redirectTo: 'global-categories-settings',
      },
      {
        path: 'import-rules',
        loadComponent: () => import('../pages/quickbooks/import/import.component')
        .then(x => x.ImportComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [clientSelectedGuard],
      },
      {
        path: 'default-rules',
        loadComponent: () => import('../pages/accounting/default-rules/default-rules.component')
        .then(x => x.DefaultRulesComponent),
        data: {
          security: SecurityCondition.hasRole(OTHER_THAN_CLIENT),
          accountingPlatform,
        },
        canActivate: [customerHasConnection],
      },
      {
        path: '**',
        redirectTo: 'transactions'
      },
    ],
  }
}
